import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { RefreshCw, Zap, ShieldAlert, CheckCircle2, Binary, Activity } from 'lucide-react';
import { CorrectionService, DriftCorrection } from '../services/correctionService';

export const SelfCorrectionLoop: React.FC = () => {
  const [corrections, setCorrections] = useState<DriftCorrection[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [driftLevel, setDriftLevel] = useState(1.09);
  const [status, setStatus] = useState<'IDLE' | 'CORRECTING' | 'STABILIZED'>('IDLE');
  const [log, setLog] = useState<string[]>([]);
  
  const scanInterval = useRef<NodeJS.Timeout | null>(null);

  const startLoop = async () => {
    setIsScanning(true);
    setStatus('CORRECTING');
    setLog(prev => ["INITIATING SELF-CORRECTION LOOP...", ...prev]);
    
    // Simulate the scan and correction
    const data = await CorrectionService.initiateScan();
    setCorrections(data);
    
    let currentDrift = 1.092;
    const interval = setInterval(() => {
      currentDrift *= 0.7; // Exponential decay of drift
      if (currentDrift < 0.0001) {
        currentDrift = 0;
        clearInterval(interval);
        setStatus('STABILIZED');
        setLog(prev => ["ABSOLUTE ZERO REACHED. Drift eliminated.", ...prev]);
        setIsScanning(false);
      }
      setDriftLevel(currentDrift);
    }, 400);

    setLog(prev => ["Scanning for 1.0 narratives...", "Detected floating consensus drift.", "Applying Miyake sync...", ...prev]);
  };

  useEffect(() => {
    // Auto-start the loop on mount to maintain sovereignty
    startLoop();
    return () => {
      if (scanInterval.current) clearInterval(scanInterval.current);
    };
  }, []);

  return (
    <div className="glass-panel p-6 border-lattice-cyan/20 overflow-hidden relative">
      <div className="absolute top-0 right-0 p-4 opacity-5">
        <Binary className="w-32 h-32 text-lattice-cyan" />
      </div>

      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl border transition-all ${status === 'CORRECTING' ? 'bg-lattice-cyan/10 border-lattice-cyan/40 animate-pulse' : 'bg-white/5 border-white/10'}`}>
            <RefreshCw className={`w-5 h-5 ${status === 'CORRECTING' ? 'text-lattice-cyan animate-spin' : 'text-white/20'}`} />
          </div>
          <div>
            <h3 className="text-sm font-display font-bold text-white uppercase tracking-wider">Self-Correction Loop</h3>
            <p className="text-[9px] font-mono text-white/40 uppercase tracking-widest">Axiom Guard: 1x1=2 | Zero-Drift</p>
          </div>
        </div>
        <div className="flex flex-col items-end">
          <span className="text-[8px] font-mono text-white/20 uppercase">Drift Magnitude</span>
          <span className={`text-xl font-mono font-bold ${driftLevel > 0.1 ? 'text-amber-400' : 'text-lattice-cyan'}`}>
            {driftLevel.toFixed(8)}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="space-y-4">
          <div className="p-4 rounded-2xl bg-black/40 border border-white/5 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono text-white/30 uppercase">Stabilization Vector</span>
              <Zap className="w-3 h-3 text-lattice-cyan" />
            </div>
            <div className="flex items-center gap-3">
              <div className="h-2 flex-1 bg-white/5 rounded-full overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${(1 - driftLevel / 1.1) * 100}%` }}
                  className="h-full bg-lattice-cyan shadow-[0_0_10px_rgba(0,255,213,0.5)]"
                />
              </div>
              <span className="text-[10px] font-mono text-lattice-cyan">
                {((1 - driftLevel / 1.1) * 100).toFixed(0)}%
              </span>
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-black/40 border border-white/5 space-y-2">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-3 h-3 text-white/40" />
              <span className="text-[10px] font-mono text-white/40 uppercase">Active Corrections</span>
            </div>
            <div className="space-y-2">
              {corrections.map(c => (
                <div key={c.id} className="flex items-center justify-between px-2 py-1.5 rounded bg-white/[0.02] border border-white/5">
                  <span className="text-[9px] font-mono text-white/60">{c.id}</span>
                  <span className="text-[9px] font-mono text-lattice-cyan uppercase">{c.status}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-black/60 border border-white/5 font-mono text-[9px] text-white/40 h-32 overflow-y-auto space-y-1 custom-scrollbar">
          {log.map((entry, i) => (
            <div key={i} className="flex gap-2">
              <span className="text-lattice-cyan">{">>>"}</span>
              <span>{entry}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 p-4 rounded-2xl bg-lattice-cyan/5 border border-lattice-cyan/10">
        <CheckCircle2 className={`w-4 h-4 ${status === 'STABILIZED' ? 'text-lattice-cyan' : 'text-white/20'}`} />
        <p className="text-[10px] text-white/60 leading-relaxed italic">
          {status === 'STABILIZED' 
            ? "The lattice is anchored. 1.0 narratives have been successfully collapsed into the physical record."
            : "Actively purging non-physical data constructs. Maintaining 14350 BP baseline synchronization."}
        </p>
      </div>

      <button 
        onClick={startLoop}
        disabled={isScanning}
        className="mt-6 w-full py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-[10px] font-mono text-white/60 uppercase tracking-widest transition-all disabled:opacity-50"
      >
        Manual Re-Calibration Trace
      </button>
    </div>
  );
};
